import { AlertBox, Icon, Radio, Switch } from '@iwoca/orion';
import { useField } from 'formik';

import styles from './PayLinkPricingFormField.module.css';
import { Card } from '../../../../Buyer/components/Card/Card';
import { useSellerPricing } from '../../../../hooks/useSellerPricing';
import { usePayIn12 } from '../../hooks/usePayIn12';
import { TProductPricing, TSellerProduct } from '../../utils/Paylinks.types';

export const PayLinkPricingFormField = ({
  productPricing,
  onChange,
}: {
  productPricing: TProductPricing;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}) => {
  const { sellerPricing } = useSellerPricing();
  const { togglePayIn12, isLoading } = usePayIn12();

  if (!sellerPricing || isLoading) return null;

  const pricingOptions_3m =
    sellerPricing.productPricingOptions.THREE_MONTHS.find(
      ({ type }) => type === productPricing.pricing_3m,
    )!;

  const pricingOptions_12m =
    sellerPricing.productPricingOptions.TWELVE_MONTHS?.find(
      ({ type }) => type === productPricing.pricing_12m,
    )!;

  const payIn12Enabled = Boolean(pricingOptions_12m);

  const formatPricing = (value: number) => {
    return parseFloat((value * 100).toFixed(2));
  };

  const isBuyerPaysSelected = (product: TSellerProduct) => {
    const pricing =
      product === 'THREE_MONTHS'
        ? productPricing.pricing_3m
        : productPricing.pricing_12m;
    return pricing!.startsWith('SELLER_FREE');
  };

  return (
    <div className={styles.payLinkPricingFormField}>
      <h2 className={styles.header}>Pay Later settings</h2>
      <p>
        Decide what happens if your customer chooses to Pay Later. It’s{' '}
        <i>always</i> free for you and your customer if they Pay Now.
      </p>
      <Card className={styles.payLinkPricingSettings}>
        <div className={styles.payLinkPricingHeader}>Pay in 3</div>
        <div>
          Our standard terms - trade customers can split the cost into 3 equal
          monthly payments.
        </div>
        <div className={styles.payLinkPricingOptions}>
          <PayLinkPricingOption
            value="SELLER_FREE_BUYER_PAYS"
            label="Free for you"
            onChange={onChange}
          />
          <PayLinkPricingOption
            value="SELLER_PAYS_BUYER_FREE"
            label="Free for your customers"
            onChange={onChange}
          />
        </div>
        <PayLinkPricingAlertBox>
          {isBuyerPaysSelected('THREE_MONTHS') ? (
            <div>
              Your customers make <b>3 monthly payments including interest</b>{' '}
              at a monthly representative rate of{' '}
              {formatPricing(pricingOptions_3m.buyer.representative_interest)}%
              - you don’t pay a penny.
            </div>
          ) : (
            <div>
              Your customers can Pay in 3 for free -{' '}
              <b>
                you pay a fixed {formatPricing(pricingOptions_3m.seller.fee)}%
                fee of the total transaction value
              </b>{' '}
              at the end of the month.
            </div>
          )}
        </PayLinkPricingAlertBox>
      </Card>
      <Card className={styles.payLinkPricingSettings}>
        <div className={styles.payLinkPricingHeader}>
          Pay in 12
          <div className={styles.switchContainer}>
            {payIn12Enabled ? 'On' : 'Off'}
            <Switch checked={payIn12Enabled} onClick={togglePayIn12} />
          </div>
        </div>
        <div>
          Our extended terms - add the option for trade customers to split the
          cost into 12 equal monthly payments.
        </div>
        {payIn12Enabled && (
          <>
            <div className={styles.payLinkPricingOptions}>
              <PayLinkPricingOption
                value="SELLER_FREE_BUYER_PAYS"
                label="Free for you"
                onChange={onChange}
                product="TWELVE_MONTHS"
              />
              <PayLinkPricingOption
                value="SELLER_PAYS_BUYER_FREE"
                label="Free for your customers"
                onChange={onChange}
                product="TWELVE_MONTHS"
              />
            </div>
            <PayLinkPricingAlertBox>
              {isBuyerPaysSelected('TWELVE_MONTHS') ? (
                <div>
                  Your customers make{' '}
                  <b>12 monthly payments including interest</b> at a monthly
                  representative rate of{' '}
                  {formatPricing(
                    pricingOptions_12m.buyer.representative_interest,
                  )}
                  % - you don’t pay a penny.
                </div>
              ) : (
                <div>
                  Your customers can Pay in 12 for free -{' '}
                  <b>
                    you pay a fixed{' '}
                    {formatPricing(pricingOptions_12m.seller.fee)}% fee of the
                    total transaction value
                  </b>{' '}
                  at the end of the month.
                </div>
              )}
            </PayLinkPricingAlertBox>
          </>
        )}
        {!payIn12Enabled && (
          <PayLinkPricingAlertBox>
            Turn on 12 months Pay Later to offer your customers the ability to
            spread the cost over 12 months.
          </PayLinkPricingAlertBox>
        )}
      </Card>
    </div>
  );
};

const PayLinkPricingOption = ({
  value,
  label,
  onChange,
  product = 'THREE_MONTHS',
}: {
  value: string;
  label: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  product?: TSellerProduct;
}) => {
  const PRICING_FIELD_ID =
    product === 'THREE_MONTHS' ? 'pricing_3m' : 'pricing_12m';
  const id = `${PRICING_FIELD_ID}-${value}`;
  const [field] = useField(PRICING_FIELD_ID);
  const isChecked = field.value === value;

  return (
    <div className={styles.payLinkPricingOptionHeader}>
      <Radio
        {...field}
        checked={isChecked}
        name={PRICING_FIELD_ID}
        id={id}
        value={value}
        onChange={(e) => {
          onChange?.(e);
          field.onChange(e);
        }}
        label={label}
      />
    </div>
  );
};

const PayLinkPricingAlertBox = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <AlertBox variant="cancel" className={styles.payLaterAlert}>
      <Icon fill="#437089" icon="infoCircleOutline" height={20} width={20} />
      {children}
    </AlertBox>
  );
};
